import { createContext, useContext } from "react";
import { useAppDispatch, useAppSelector } from "@store";
import { handleRedo, handleUndo } from "@store/story";
import {
  selectCanUndoStory,
  selectTopAiActionId,
} from "@store/story/history-selectors";

export type StoryHistory = {
  canUndo: boolean;
  canRedo: boolean;
  // The aiActionId on the newest undo step — the one undo() would pop — or
  // null when that step is a user edit or the stack is empty. The AI Undo
  // affordances (TurnFooter and AddedSlideCard's Undo, the skill chip's
  // decline, the agent's undo command) may only fire while this is their own turn's
  // id; see
  // ai/hooks/use-can-revert-turn.ts. Same truth as selectTopAiActionId.
  topAiActionId: string | null;
  undo: () => void;
  redo: () => void;
};

// `active` distinguishes a live collaborative room (Yjs UndoManager backs the
// history) from single-seat editing (Redux history). YjsStoryProvider supplies
// a value with active:true; everywhere else the default below leaves undo/redo
// on the Redux path.
export type CollabStoryHistory = StoryHistory & { active: boolean };

const defaultValue: CollabStoryHistory = {
  active: false,
  canUndo: false,
  canRedo: false,
  topAiActionId: null,
  undo: () => {},
  redo: () => {},
};

export const StoryHistoryContext = createContext<CollabStoryHistory>(defaultValue);

// Single undo/redo API for the story, regardless of mode. In a collaborative
// room it is backed by the Yjs UndoManager (local-scoped, conflict-free, see
// undo-manager.ts); otherwise it falls back to the single-user Redux history.
// Call sites (toolbar buttons, ⌘Z/⌘Y shortcut, header menu) stay mode-agnostic.
export const useStoryHistory = (): StoryHistory => {
  const collab = useContext(StoryHistoryContext);
  const dispatch = useAppDispatch();
  // Redux fallback selectors run unconditionally (hooks rules); they're only
  // returned when no collaborative history is active. The shared selectors
  // (history-selectors.ts) read the same Redux history here — and in a room
  // they read the provider's mirror of the very stack `collab` reports, so
  // code outside this context (the agent's undo command) agrees with it.
  const reduxCanUndo = useAppSelector(selectCanUndoStory);
  const reduxCanRedo = useAppSelector((s) => s.story.history.future.length > 0);
  const reduxTopAiActionId = useAppSelector(selectTopAiActionId);

  if (collab.active) {
    return {
      canUndo: collab.canUndo,
      canRedo: collab.canRedo,
      topAiActionId: collab.topAiActionId,
      undo: collab.undo,
      redo: collab.redo,
    };
  }

  return {
    canUndo: reduxCanUndo,
    canRedo: reduxCanRedo,
    topAiActionId: reduxTopAiActionId,
    undo: () => dispatch(handleUndo()),
    redo: () => dispatch(handleRedo()),
  };
};
